import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button } from '../atoms';
import { ActivityPanel, ActivityEvent, SavingsEmailModal, GenerateEmailModal, CalculateFeesModal, FollowButton, EntityGroupsPanel, EntityActivityPanel, InlineEditCard, LastUpdated, useToast } from '../organisms';
import { AppealAdvanceModal } from '../organisms/AppealAdvanceModal';
import { EntityHeader } from '../shell/EntityHeader';
import { STAGE_ORDER, STAGE_DATE_FIELD, stageLabel, stageVariant } from '../lib/appeal-stages';
import { useEntityPermission } from '../lib/usePermissions';
import { useIsCompact } from '../lib/useMediaQuery';
import { formatDate, formatDateTime } from '../lib/orgTimezone';

/**
 * AppealDetail — /appeals/:id
 *
 * Vertical lifecycle timeline (every canonical stage, past → current →
 * future) with the relevant dates shown against reached stages, an
 * "Advance stage" action, and the appeal's own activity feed.
 */

interface Appeal {
  id: string;
  parcel_id: string | null;
  tax_year: number | null;
  lifecycle_stage: string;
  resolution_path: string | null;
  hearing_date: string | null;
  /**
   * B4 — is `hearing_date` a placeholder or firm? Optional on the wire so an older
   * cached row still parses; anything but an explicit 'tentative' reads as firm.
   */
  hearing_date_confidence?: 'tentative' | 'confirmed' | null;
  board_order_expected_date: string | null;
  board_order_received_date: string | null;
  litigation_deadline_date: string | null;
  outcome: string | null;
  settled_value: number | null;
  reduction: number | null;
  consultant_id: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
}

interface ParcelLite {
  account_number?: string | null;
  name?: string | null;
  owner_name?: string | null;
}

interface ActivityApiEvent {
  id: string;
  timestamp: string;
  actor?: string | null;
  action: string;
  message: string;
}

export function AppealDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [savingsOpen, setSavingsOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [calcFeesOpen, setCalcFeesOpen] = useState(false);
  // The savings-email routes gate on the 'notification' entity (can_add to draft).
  // Mirror that so the control only renders for a user the server would accept.
  const canDraftSavings = useEntityPermission('notification', 'can_add');
  const canEditAppeal = useEntityPermission('appeal', 'can_edit');
  // calculate-fees stages a draft invoice → gate on the 'invoice' entity (can_add),
  // the closest grid proxy for the server's invoice PROCESS gate.
  const canCalculateFees = useEntityPermission('invoice', 'can_add');
  // Advance-stage POSTs /appeals/:id/advance (backend permGate 'appeal' 'PUT') — gate the
  // button on the same capability so a view-only role (training/agent) never sees a
  // control that would 403. It was rendered unconditionally.
  const canAdvanceAppeal = useEntityPermission('appeal', 'can_edit');
  // Below the desktop breakpoint the fixed 360px side rail would force horizontal
  // overflow, so the two-column layout collapses to a single stacked column.
  const isCompact = useIsCompact();

  const appealQuery = useQuery<Appeal>({
    queryKey: ['appeal', id],
    queryFn: ({ signal }) => api.get<Appeal>(`/api/appeals/${id}`, signal),
    enabled: !!id,
  });

  const parcelId = appealQuery.data?.parcel_id ?? undefined;
  const parcelQuery = useQuery<ParcelLite>({
    queryKey: ['parcel', parcelId],
    queryFn: ({ signal }) => api.get<ParcelLite>(`/api/parcels/${parcelId}`, signal),
    enabled: !!parcelId,
  });

  const activityQuery = useQuery<{ events: ActivityApiEvent[] }>({
    queryKey: ['appeal-activity', id],
    queryFn: ({ signal }) => api.get<{ events: ActivityApiEvent[] }>(`/api/activity/entity/appeals/${id}`, signal),
    enabled: !!id,
  });

  if (appealQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading appeal…</p>;
  }

  if (appealQuery.error) {
    const notFound = appealQuery.error instanceof ApiClientError && appealQuery.error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>{notFound ? 'Appeal not found' : 'Failed to load appeal'}</h2>
        <p style={{ margin: 0 }}>{(appealQuery.error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate('/appeals/queues')}>
            ← Back to worklist
          </Button>
        </p>
      </div>
    );
  }

  const appeal = appealQuery.data;
  if (!appeal) return null;

  const parcel = parcelQuery.data;
  const parcelLabel = parcel?.account_number || parcel?.name || parcel?.owner_name || (parcelId ? parcelId.slice(0, 8) : 'Parcel');
  const title = `${parcelLabel}${appeal.tax_year ? ` · ${appeal.tax_year}` : ''}`;

  const activityEvents: ActivityEvent[] = (activityQuery.data?.events ?? []).map(ev => ({
    id: ev.id,
    kind: ev.action === 'delete' ? 'danger' : ev.action === 'create' ? 'success' : 'info',
    title: ev.message,
    description: ev.actor || 'System',
    timestamp: ev.timestamp ? formatDateTime(ev.timestamp) : '',
  }));

  return (
    <>
      <EntityHeader
        prefix="Appeal"
        title={title}
        subtitle={<LastUpdated updatedBy={appeal.updated_by} updatedAt={appeal.updated_at} />}
        statusPills={<Badge variant={stageVariant(appeal.lifecycle_stage)}>{stageLabel(appeal.lifecycle_stage)}</Badge>}
        actions={
          <>
            {canDraftSavings && (
              <Button variant="secondary" onClick={() => setSavingsOpen(true)}>
                Savings email
              </Button>
            )}
            {canDraftSavings && (
              <Button variant="secondary" onClick={() => setEmailOpen(true)}>
                Generate email
              </Button>
            )}
            {canCalculateFees && (
              <Button variant="secondary" onClick={() => setCalcFeesOpen(true)}>
                Calculate fees
              </Button>
            )}
            {canAdvanceAppeal && (
              <Button variant="primary" onClick={() => setAdvanceOpen(true)}>
                Advance stage
              </Button>
            )}
            <FollowButton entityType="appeal" entityId={appeal.id} />
          </>
        }
      />

      <div style={{
        display: 'grid',
        gridTemplateColumns: isCompact ? '1fr' : 'minmax(0, 1fr) 360px',
        gap: 'var(--sp-5)',
        alignItems: 'start',
      }}>
        <StageTimeline appeal={appeal} canEdit={canAdvanceAppeal} />
        <ActivityPanel
          title="Appeal history"
          events={activityEvents}
          emptyMessage={activityQuery.isLoading ? 'Loading history…' : 'No activity recorded yet.'}
        />
      </div>

      <div style={{ marginTop: 'var(--sp-5)' }}>
        {/* 196 (ATA part 4 §4a): the recommendation and litigation half of the appeal, quoted by
            the Appeal Recommendation, Litigation Settlement Results and Refund Check emails. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 'var(--sp-4)', marginTop: 'var(--sp-5)' }}>
          <InlineEditCard
            title="Recommendation" routePath="appeals" recordId={appeal.id} record={appeal as unknown as Record<string, unknown>} canEdit={canEditAppeal} invalidateKeys={[['appeal', appeal.id]]}
            fields={[
              { key: 'recommendation', label: 'Recommendation', type: 'select', preserveOrder: true, options: [{ value: 'no_appeal', label: 'Recommend no appeal' }, { value: 'consider_appeal', label: 'Recommend consider appeal' }, { value: 'appeal', label: 'Recommend appeal' }] },
              { key: 'recommendation_narrative', label: 'Narrative', type: 'textarea' },
            ]}
          />
          <InlineEditCard
            title="Litigation" routePath="appeals" recordId={appeal.id} record={appeal as unknown as Record<string, unknown>} canEdit={canEditAppeal} invalidateKeys={[['appeal', appeal.id]]}
            fields={[
              { key: 'arb_result_value', label: 'ARB result value', type: 'number' },
              { key: 'max_settlement_authority', label: 'Maximum settlement authority', type: 'number' },
              { key: 'settlement_date', label: 'Settlement date', type: 'date' },
              { key: 'cause_no', label: 'Final judgment cause number' },
              { key: 'judgment_entered_date', label: 'Judgment entered', type: 'date' },
              { key: 'estimated_refund_amount', label: 'Estimated refund', type: 'number' },
            ]}
          />
        </div>
        <EntityGroupsPanel entityType="appeal" entityId={appeal.id} />
        {/* spec 0014: the appeal's one stream (comments, emails, events) with the Comment / Email composer. */}
        <section aria-label="Activity" style={{ marginTop: 'var(--sp-5)' }}>
          <h2 style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)' }}>Activity</h2>
          <EntityActivityPanel entityType="appeals" entityId={appeal.id} />
        </section>
      </div>

      {id && (
        <AppealAdvanceModal
          appealId={id}
          currentStage={appeal.lifecycle_stage}
          open={advanceOpen}
          onClose={() => setAdvanceOpen(false)}
          onAdvanced={() => { /* queries invalidated inside the modal */ }}
        />
      )}

      {id && (
        <SavingsEmailModal
          appealId={id}
          open={savingsOpen}
          onClose={() => setSavingsOpen(false)}
        />
      )}

      {id && (
        <GenerateEmailModal
          open={emailOpen}
          onClose={() => setEmailOpen(false)}
          scope={{ entityType: 'appeals', entityId: id }}
        />
      )}

      {id && (
        <CalculateFeesModal
          appealId={id}
          open={calcFeesOpen}
          onClose={() => setCalcFeesOpen(false)}
        />
      )}
    </>
  );
}

/**
 * B4 — is the appeal's hearing date still a placeholder? Anything but an explicit
 * 'tentative' reads as firm, including a row from before the column existed, so
 * the distinction can only make a date LESS authoritative than today, never more.
 */
function hearingIsTentative(appeal: Appeal): boolean {
  return appeal.hearing_date_confidence === 'tentative';
}

function StageTimeline({ appeal, canEdit }: { appeal: Appeal; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currentIndex = STAGE_ORDER.indexOf(appeal.lifecycle_stage as (typeof STAGE_ORDER)[number]);
  const isWithdrawn = appeal.lifecycle_stage === 'withdrawn';

  // B4 — confirm a tentative hearing date (or put a confirmed one back in question
  // when the board resets it). Its own compare-and-swap endpoint, audit-logged.
  //
  // It deliberately does NOT move the litigation deadline: under the shipped
  // assumption for OQ-2 the litigation window is anchored on the BOARD ORDER, not
  // the hearing (see src/config/hearing-date-rules.js — that assumption is the
  // single constant LITIGATION_DEADLINE_ANCHOR and is pending Yadi's ruling).
  const confidenceMut = useMutation({
    mutationFn: (to: 'tentative' | 'confirmed') =>
      api.post<Appeal>(`/api/calendar/appeal-hearings/${appeal.id}/date-confidence`, { to }),
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ['appeal', appeal.id] });
      queryClient.invalidateQueries({ queryKey: ['appeal-activity', appeal.id] });
      toast({
        variant: 'success',
        message: row.hearing_date_confidence === 'confirmed' ? 'Hearing date confirmed.' : 'Hearing date marked tentative.',
      });
    },
    onError: (err) => toast({
      variant: 'error',
      message: err instanceof ApiClientError ? err.message : 'Could not update the hearing date.',
    }),
  });

  return (
    <section style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      boxShadow: 'var(--sh-sm)',
      padding: 'var(--sp-5)',
    }}>
      <h3 style={{
        margin: '0 0 var(--sp-4)',
        fontSize: 'var(--fs-md)',
        fontWeight: 'var(--fw-semibold)',
        color: 'var(--color-ink)',
      }}>Lifecycle</h3>

      {isWithdrawn && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <Badge variant="neutral">Withdrawn</Badge>
        </div>
      )}

      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {STAGE_ORDER.map((stage, i) => {
          const state: 'done' | 'current' | 'future' =
            !isWithdrawn && i < currentIndex ? 'done'
              : !isWithdrawn && i === currentIndex ? 'current'
                : 'future';
          const dateField = STAGE_DATE_FIELD[stage];
          const dateValue = dateField ? (appeal[dateField as keyof Appeal] as string | null) : null;
          const hearingRow = dateField === 'hearing_date';
          const isLast = i === STAGE_ORDER.length - 1;

          const markerColor =
            state === 'done' ? 'var(--brand-primary)'
              : state === 'current' ? 'var(--brand-primary)'
                : 'var(--color-border-strong)';

          return (
            <li key={stage} style={{ display: 'flex', gap: 'var(--sp-3)' }}>
              {/* marker + connector rail */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <span style={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  flexShrink: 0,
                  marginTop: 2,
                  background: state === 'future' ? 'var(--color-card)' : markerColor,
                  border: `2px solid ${markerColor}`,
                  boxShadow: state === 'current' ? '0 0 0 4px var(--brand-primary-bg)' : 'none',
                }} />
                {!isLast && (
                  <span style={{
                    flex: 1,
                    width: 2,
                    minHeight: 24,
                    background: state === 'done' ? 'var(--brand-primary)' : 'var(--color-border)',
                  }} />
                )}
              </div>

              {/* label + date */}
              <div style={{ paddingBottom: isLast ? 0 : 'var(--sp-4)' }}>
                <div style={{
                  fontSize: 'var(--fs-md)',
                  fontWeight: state === 'current' ? 'var(--fw-semibold)' : 'var(--fw-medium)',
                  color: state === 'future' ? 'var(--color-muted)' : 'var(--color-ink)',
                }}>
                  {stageLabel(stage)}
                  {state === 'current' && (
                    <span style={{ marginLeft: 'var(--sp-2)' }}>
                      <Badge variant={stageVariant(stage)}>Current</Badge>
                    </span>
                  )}
                </div>
                {dateValue && (
                  <div style={{
                    fontSize: 'var(--fs-sm)',
                    color: 'var(--color-ink-3)',
                    marginTop: 'var(--sp-0-5)',
                    display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap',
                  }}>
                    <span style={hearingRow && hearingIsTentative(appeal)
                      ? { borderBottom: '1px dashed var(--color-status-amber, var(--color-border))' }
                      : undefined}
                    >{formatDate(dateValue.slice(0, 10))}</span>
                    {/* B4 — the hearing date, and only the hearing date, carries a
                        tentative/confirmed standing. A placeholder must READ as one:
                        staff plan around this date. */}
                    {hearingRow && hearingIsTentative(appeal) && <Badge variant="warning">Tentative</Badge>}
                    {hearingRow && canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={confidenceMut.isPending}
                        onClick={() => confidenceMut.mutate(hearingIsTentative(appeal) ? 'confirmed' : 'tentative')}
                      >
                        {confidenceMut.isPending
                          ? 'Saving…'
                          : hearingIsTentative(appeal) ? 'Confirm date' : 'Mark tentative'}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
